import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { formatUnixDate } from '@/shared/lib/datetime';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DetailLayout } from '@/shared/ui/DetailLayout';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { CRYPTO_STATUS, changeCryptoStatus, fetchCryptoItem, fetchRejectReasons, type CryptoItem } from './api';

/** Legacy shows crypto requests to this account only. */
const CRYPTO_VIEWER_EMAIL = 'palshina.yb@uremont.com';

const FIELDS: { label: string; value: (item: CryptoItem) => string | number | undefined }[] = [
  { label: 'Фамилия', value: (i) => i.second_name },
  { label: 'Имя', value: (i) => i.first_name },
  { label: 'Отчество', value: (i) => i.last_name },
  { label: 'Телефон', value: (i) => i.phone },
  { label: 'Электронная почта', value: (i) => i.email },
  { label: 'Номер кошелька', value: (i) => i.wallet_number },
  { label: 'Дата рождения', value: (i) => formatUnixDate(i.birthday) },
  { label: 'Гражданство', value: (i) => i.citizenship },
  { label: 'Место рождения', value: (i) => i.place_of_birth },
  { label: 'Серия', value: (i) => i.passport_series },
  { label: 'Номер', value: (i) => i.passport_number },
  { label: 'Дата выдачи', value: (i) => formatUnixDate(i.passport_date_of_issue) },
  { label: 'Кем выдан', value: (i) => i.passport_organization_issued },
];

const PASSPORT_WIDTH = 500;

const PassportPhoto = ({ src }: { src?: string }) => {
  const [isMirrored, setIsMirrored] = useState(false);
  if (!src) return null;
  return (
    <>
      <button type="button" className="btn btn-default btn-sm" onClick={() => setIsMirrored((v) => !v)}>
        Перевернуть
      </button>
      <div style={{ marginTop: 10 }}>
        <img
          src={src}
          alt=""
          style={{ width: PASSPORT_WIDTH, maxWidth: '100%', transform: isMirrored ? 'scaleX(-1)' : undefined }}
        />
      </div>
    </>
  );
};

type ReviewProps = { item: CryptoItem };

const StatusActions = ({ item }: ReviewProps) => {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'verify' | 'reject' | null>(null);
  const [reasonId, setReasonId] = useState('');
  const reasons = useQuery({ queryKey: ['crypto-reject-reasons'], queryFn: fetchRejectReasons });

  const close = () => {
    setDialog(null);
    setReasonId('');
  };
  const mutation = useMutation({
    mutationFn: (v: { statusId: number; commentId?: string }) => changeCryptoStatus(item.id, v.statusId, v.commentId),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['crypto'] });
      void qc.invalidateQueries({ queryKey: ['crypto-item', item.id] });
    },
    onError: () => window.alert('Не удалось изменить статус'),
  });

  switch (Number(item.status?.value)) {
    case CRYPTO_STATUS.VERIFIED:
      return (
        <button type="button" className="btn btn-success" disabled>
          Верифицирован
        </button>
      );
    case CRYPTO_STATUS.REJECTED:
      return (
        <button type="button" className="btn btn-danger" disabled>
          Отклонен
        </button>
      );
    case CRYPTO_STATUS.NEW:
      return (
        <>
          <button type="button" className="btn btn-success" style={{ marginRight: 8 }} onClick={() => setDialog('verify')}>
            Верифицировать
          </button>
          <button type="button" className="btn btn-danger" onClick={() => setDialog('reject')}>
            Отклонить
          </button>
          {dialog === 'verify' && (
            <ModalDialog
              title="Вы уверены, что хотите верифицировать пользователя?"
              onClose={close}
              isSaving={mutation.isPending}
              onSave={() => mutation.mutate({ statusId: CRYPTO_STATUS.VERIFIED })}
            >
              <p>Статус заявки будет изменён на «Верифицирован».</p>
            </ModalDialog>
          )}
          {dialog === 'reject' && (
            <ModalDialog
              title="Выберите причину отказа в верификации пользователя"
              onClose={close}
              isSaving={mutation.isPending || !reasonId}
              onSave={() => mutation.mutate({ statusId: CRYPTO_STATUS.REJECTED, commentId: reasonId })}
            >
              <FilterSelect
                options={reasons.data ?? []}
                value={reasonId}
                isLoading={reasons.isLoading}
                isClearable={false}
                width={400}
                onChange={setReasonId}
              />
            </ModalDialog>
          )}
        </>
      );
    default:
      return null;
  }
};

export const CryptoDetailPage = () => {
  const id = useRouteId();
  const email = useAuthStore((s) => s.profile.email);
  const { data, isLoading, error } = useQuery({
    queryKey: ['crypto-item', id],
    queryFn: () => fetchCryptoItem(id),
    enabled: email === CRYPTO_VIEWER_EMAIL,
  });

  if (email !== CRYPTO_VIEWER_EMAIL) return <Navigate to="/crypto" replace />;

  return (
    <DetailLayout title={`Crypto #${id}`} isLoading={isLoading} error={error}>
      <p>
        <Link to="/crypto">⇐ Вернуться назад</Link>
      </p>
      {data && (
        <>
          {FIELDS.map(({ label, value }) => (
            <FormField key={label} label={label}>
              <input className="form-control input-sm" style={{ maxWidth: 500 }} value={value(data) ?? ''} disabled />
            </FormField>
          ))}
          <FormField label="Фото паспорта">
            <PassportPhoto src={data.image?.src} />
          </FormField>
          <FormField label="Комментарий">
            <textarea className="form-control" style={{ maxWidth: 500 }} value={data.comments ?? ''} disabled />
          </FormField>
          <StatusActions item={data} />
        </>
      )}
    </DetailLayout>
  );
};
